import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { all_routes } from '../../router/all_routes'
import { useAuth } from '../../../provider/AuthProvider'
import { getCBTDetail, startCBT } from '../../../api/courses'

// Data untuk petunjuk umum dan tips
const mockCBTData = {
  generalInstructions: [
    'Gunakan perangkat yang sudah memenuhi spesifikasi minimum.',
    'Koneksi internet harus stabil, disarankan menggunakan Wi-Fi atau jaringan kabel.',
    'Pastikan perangkat memiliki daya cukup (baterai penuh atau sambungkan ke charger).',
    'Dilarang membuka aplikasi lain selain sistem ujian selama tes berlangsung.',
    'Kerjakan soal dengan benar dan dengan durasi yang telah ditentukan.',
    'Jika anda menutup tab, maka jawaban akan hilang dan konten tidak bisa diakses lagi.',
    'Apabila mengalami kendala teknis, segera hubungi panitia/teknisi melalui kontak yang disediakan.',
  ],
  tips: [
    'Lakukan cek perangkat & koneksi minimal 1 hari sebelum ujian.',
    'Tutup aplikasi lain (WhatsApp, Telegram, Zoom, dll) agar tidak mengganggu.',
    'Gunakan browser terbaru (Chrome, Edge, Firefox).',
    'Siapkan ruangan yang tenang dan bebas gangguan.',
    'Baca instruksi soal dengan teliti sebelum menjawab.',
    'Tandai soal yang ragu dengan fitur “Ragu” untuk dikerjakan belakangan.',
    'Cek kembali jawaban sebelum menekan tombol Submit.',
    'Usahakan tetap tenang dan fokus, jangan panik jika ada kendala—langsung hubungi helpdesk.',
  ],
}

const CBTInstruction = () => {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth();
  const navigate = useNavigate()
  const [isStarting, setIsStarting] = useState(false)
  const [attemptInfo, setAttemptInfo] = useState({
    maxAttempt: 0,
    usedAttempt: 0,
  })

  useEffect(() => {
    const fetchAttemptInfo = async () => {
      if (!id) return
      try {
        const response = await getCBTDetail(id)
        const maxAttempt = Number(response.data.max_attempt || 0)
        const usedAttempt = Number(response.data.user_attempt_count || 0)
        setAttemptInfo({ maxAttempt, usedAttempt })
      } catch {
        setAttemptInfo({ maxAttempt: 0, usedAttempt: 0 })
      }
    }

    fetchAttemptInfo()
  }, [id])

  const remainingAttempt = Math.max(
    0,
    attemptInfo.maxAttempt - attemptInfo.usedAttempt,
  )

  const handleStartTest = async () => {
    // Hit API Start Attempt
    await startCBT({ course_id: id.toString(), user_id: user?.id.toString() });

    setIsStarting(true)
    // Simulate loading
    setTimeout(() => {
      navigate(all_routes.studentCBTTest.replace(':id', id || ''))
    }, 1000)
  }

  return (
    <>
      <div className="content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card">
                <div className="card-header bg-primary text-white">
                  <h4 className="mb-0">
                    <i className="isax isax-document-text me-2" />
                    Petunjuk Ujian CBT
                  </h4>
                </div>
                <div className="card-body">
                  {/* Petunjuk Umum */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">
                      <i className="isax isax-info-circle me-2 text-info" />
                      Petunjuk Umum
                    </h6>
                    <ul className="list-unstyled">
                      {mockCBTData.generalInstructions.map(
                        (instruction, index) => (
                          <li key={index} className="mb-2">
                            <i className="isax isax-tick-circle text-success me-2" />
                            {instruction}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>

                  {/* Tips Sebelum & Saat Ujian */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">
                      <i className="isax isax-lightbulb me-2 text-warning" />
                      Tips Sebelum & Saat Ujian
                    </h6>
                    <ul className="list-unstyled">
                      {mockCBTData.tips.map((tip, index) => (
                        <li key={index} className="mb-2">
                          <i className="isax isax-tick-circle text-success me-2" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Warning */}
                  <div className="alert alert-warning">
                    <div className="d-flex">
                      <i className="isax isax-warning-2 fs-4 me-3" />
                      <div>
                        <h6 className="alert-heading">Peringatan!</h6>
                        <p className="mb-0">
                          Pastikan Anda telah mempelajari semua materi yang akan
                          diuji. Ujian hanya dapat dilakukan satu kali. Jika
                          Anda tidak lulus, Anda harus menunggu 24 jam sebelum
                          dapat mengikuti ujian ulang.
                        </p>
                      </div>
                    </div>
                  </div>

                  {attemptInfo.maxAttempt > 0 && (
                    <div className="alert alert-info">
                      <i className="isax isax-timer-1 me-2" />
                      Sisa percobaan: {remainingAttempt} dari{' '}
                      {attemptInfo.maxAttempt}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="d-flex justify-content-between align-items-center">
                    <Link
                      to={all_routes.studentCourses}
                      className="btn btn-outline-secondary"
                    >
                      <i className="isax isax-arrow-left me-2" />
                      Kembali ke Dashboard
                    </Link>
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={handleStartTest}
                      disabled={isStarting || remainingAttempt <= 0}
                    >
                      {isStarting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Memulai Ujian...
                        </>
                      ) : (
                        <>
                          <i className="isax isax-play-circle5 me-2" />
                          Mulai Ujian
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CBTInstruction
